/**
 * Zod schemas for the admin-console API endpoints consumed by
 * `@/lib/api/apiClient`'s TanStack Query hooks (GitHub issue — "Implement
 * RTK Query hooks and caching for all API endpoints").
 *
 * Follows the exact same shape/convention as `./subscriptions.ts`
 * (Requirement #4 of the immediately-preceding "Unified API client with
 * RTK Query for all frontend data fetching" issue, which this issue
 * extends): one Zod schema per real, live backend response, duplicated
 * here rather than cross-imported from `@herohire/types` (which only
 * exports plain `interface`s, not runtime-checkable Zod schemas) so a
 * shape drift between this file and `packages/types/src/admin.ts` is
 * caught by `admin.test.ts`, and so a wire-format drift between frontend
 * and backend throws at the query boundary (`.parse()`) instead of
 * silently rendering `undefined`.
 *
 * Covers:
 * - `GET /api/admin/dashboard` (`apps/workers/src/api/admin/dashboard.ts`)
 * - `GET /api/admin/customers` (`apps/workers/src/api/admin/list.ts`)
 * - `GET /api/admin/jobs` (`apps/workers/src/api/admin/list.ts`)
 * - `GET /api/admin/jobs/:jobId` (`apps/workers/src/api/admin/list.ts`)
 * - `POST /api/admin/impersonate` / `POST /api/admin/impersonate/end`
 *   (`apps/workers/src/api/admin/impersonate.ts`)
 *
 * Related: GitHub issue #284 (admin console), #281/#302 (impersonation),
 * docs/v2/08-admin-platform-ai-agent.md
 */

import { z } from 'zod';

// ========================================================================
// GET /api/admin/dashboard
// ========================================================================

/** Mirrors `AdminDashboardMetrics` (`@herohire/types`). Includes
 * `total_candidates_sent`/`total_opened`/`total_bounced` added by GitHub
 * issue #563 — see that type's doc-comments for why all three MUST share
 * the same scope (`AdminDashboardResponse.scoped_user_id`). */
export const adminDashboardMetricsSchema = z.object({
  total_customers: z.number(),
  active_customers: z.number(),
  trial_customers: z.number(),
  enterprise_customers: z.number(),
  total_jobs: z.number(),
  active_jobs: z.number(),
  total_sourcing_runs_today: z.number(),
  successful_sourcing_runs_today: z.number(),
  total_outreach_sent_today: z.number(),
  total_replies_received_today: z.number(),
  avg_reply_rate: z.number(),
  avg_open_rate: z.number(),
  avg_bounce_rate: z.number(),
  activation_rate: z.number(),
  churn_rate: z.number(),
  mrr_total: z.number(),
  mrr_growth_rate: z.number(),
  total_candidates_sent: z.number(),
  total_opened: z.number(),
  total_bounced: z.number(),
  timestamp: z.string(),
});
export type AdminDashboardMetrics = z.infer<typeof adminDashboardMetricsSchema>;

/**
 * Mirrors `CandidateFunnelStageResult` (`@herohire/types`) — GitHub issue
 * "[Dashboard] Real customer funnel breakdown (not '603 / 60 / 0')". One
 * row of the 10-stage candidate pipeline funnel (sourced -> enriched ->
 * reachable -> verified -> top-N -> queued -> sent -> opened -> replied ->
 * shortlisted), replacing the old opaque "603 candidates / 60 queued / 0
 * sent" text.
 */
export const candidateFunnelStageResultSchema = z.object({
  stage: z.enum([
    'sourced',
    'enriched',
    'reachable',
    'verified',
    'topN',
    'queued',
    'sent',
    'opened',
    'replied',
    'shortlisted',
  ]),
  label: z.string(),
  count: z.number().nullable(),
  available: z.boolean(),
  error: z.string().optional(),
  drop: z.number().nullable(),
});
export type CandidateFunnelStageResult = z.infer<typeof candidateFunnelStageResultSchema>;

/** Mirrors `AdminFunnelMetrics` (`@herohire/types`). */
export const adminFunnelMetricsSchema = z.object({
  leads: z.number(),
  signups: z.number(),
  activated: z.number(),
  paid: z.number(),
  retained: z.number(),
  conversion_lead_to_signup: z.number(),
  conversion_signup_to_activated: z.number(),
  conversion_activated_to_paid: z.number(),
  retention_rate: z.number(),
});
export type AdminFunnelMetrics = z.infer<typeof adminFunnelMetricsSchema>;

/** Mirrors `AdminDashboardResponse` (`@herohire/types`). `scoped_user_id`
 * (GitHub issue #563) echoes the `user_id` the request was scoped to, or
 * `null` for the platform-wide view. */
export const adminDashboardResponseSchema = z.object({
  metrics: adminDashboardMetricsSchema,
  candidate_funnel_breakdown: z.array(candidateFunnelStageResultSchema),
  funnel: adminFunnelMetricsSchema,
  status: z.literal('ok'),
  scoped_user_id: z.string().nullable(),
});
export type AdminDashboardResponse = z.infer<typeof adminDashboardResponseSchema>;

/**
 * Query params accepted by `GET /api/admin/dashboard` (GitHub issue
 * #563's `user_id` customer filter — `apps/workers/src/api/admin/
 * dashboard.ts`). `userId: ''` (the default) means "platform-wide,
 * unscoped" — mirrors the `''`-means-"no filter" convention already used
 * by `adminJobsQuerySchema.status`/`adminCustomersQuerySchema.plan` above.
 */
export const adminDashboardQuerySchema = z.object({
  userId: z.string().default(''),
});
export type AdminDashboardQueryParams = z.infer<typeof adminDashboardQuerySchema>;

export function buildAdminDashboardQueryString(params: AdminDashboardQueryParams): string {
  const query = new URLSearchParams();
  if (params.userId) query.set('user_id', params.userId);
  return query.toString();
}

// ========================================================================
// GET /api/admin/customers
// ========================================================================

/** Mirrors `AdminCustomerInfo` (`@herohire/types`). */
export const adminCustomerSchema = z.object({
  id: z.string(),
  uuid: z.string(),
  email: z.string(),
  name: z.string().nullable().optional(),
  company_name: z.string().nullable().optional(),
  plan: z.enum(['STARTER', 'PROFESSIONAL', 'ENTERPRISE']),
  silo_mode: z.enum(['SHARED', 'ENTERPRISE']),
  status: z.enum(['active', 'inactive', 'trial', 'suspended']),
  last_login: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  tenant_id: z.string(),
});
export type AdminCustomerInfo = z.infer<typeof adminCustomerSchema>;

/** Filter/sort/pagination params accepted by `GET /api/admin/customers`
 * (`parseListParams()` in `apps/workers/src/api/admin/list.ts`). */
export const adminCustomersQuerySchema = z.object({
  limit: z.number().int().positive().default(20),
  offset: z.number().int().nonnegative().default(0),
  search: z.string().default(''),
  plan: z.union([z.enum(['STARTER', 'PROFESSIONAL', 'ENTERPRISE']), z.literal('')]).default(''),
  status: z
    .union([z.enum(['active', 'inactive', 'trial', 'suspended']), z.literal('')])
    .default(''),
});
export type AdminCustomersQueryParams = z.infer<typeof adminCustomersQuerySchema>;

export function buildAdminCustomersQueryString(params: AdminCustomersQueryParams): string {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.search) query.set('search', params.search);
  if (params.plan) query.set('plan', params.plan);
  if (params.status) query.set('status', params.status);
  return query.toString();
}

// ========================================================================
// GET /api/admin/jobs, GET /api/admin/jobs/:jobId
// ========================================================================

/** Mirrors `AdminJobInfo` (`@herohire/types`). */
export const adminJobSchema = z.object({
  id: z.string(),
  uuid: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  owner_id: z.string(),
  owner_name: z.string().nullable().optional(),
  status: z.enum(['DRAFT', 'ACTIVE', 'PAUSED', 'CLOSED']),
  sourced_count: z.number(),
  screened_count: z.number(),
  outreach_sent: z.number(),
  replies_received: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  tenant_id: z.string(),
});
export type AdminJobInfo = z.infer<typeof adminJobSchema>;

/** Filter/sort/pagination params accepted by `GET /api/admin/jobs`. */
export const adminJobsQuerySchema = z.object({
  limit: z.number().int().positive().default(20),
  offset: z.number().int().nonnegative().default(0),
  search: z.string().default(''),
  sortBy: z.enum(['created_at', 'updated_at', 'title']).default('created_at'),
  status: z.union([z.enum(['DRAFT', 'ACTIVE', 'PAUSED', 'CLOSED']), z.literal('')]).default(''),
});
export type AdminJobsQueryParams = z.infer<typeof adminJobsQuerySchema>;

export function buildAdminJobsQueryString(params: AdminJobsQueryParams): string {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
    sort_by: params.sortBy,
  });
  if (params.search) query.set('search', params.search);
  if (params.status) query.set('status', params.status);
  return query.toString();
}

/** Generic `AdminListResponse<T>` shape (`@herohire/types`), applied to
 * a given item schema. */
export function adminListResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    data: z.array(itemSchema),
    total_count: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
    has_more: z.boolean(),
  });
}

export const adminCustomersListSchema = adminListResponseSchema(adminCustomerSchema);
export type AdminCustomersListResponse = z.infer<typeof adminCustomersListSchema>;

export const adminJobsListSchema = adminListResponseSchema(adminJobSchema);
export type AdminJobsListResponse = z.infer<typeof adminJobsListSchema>;

/** `GET /api/admin/jobs/:jobId` — a single `AdminJobInfo`, not wrapped in
 * `AdminListResponse` (see `getJobDetail()` in `list.ts`, which today
 * returns a bare `{ error }` 404 placeholder — this schema matches its
 * eventual real "found" shape, `AdminJobInfo` itself). */
export const adminJobDetailSchema = adminJobSchema;
export type AdminJobDetailResponse = z.infer<typeof adminJobDetailSchema>;

// ========================================================================
// POST /api/admin/impersonate, POST /api/admin/impersonate/end
// ========================================================================

/** Mirrors `ImpersonateResponse` (`@herohire/types`). */
export const impersonateResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.number(),
  sessionId: z.string(),
  impersonatedUserId: z.string(),
});
export type ImpersonateResponseDTO = z.infer<typeof impersonateResponseSchema>;

/** Mirrors `EndImpersonationResponse` (`@herohire/types`). */
export const endImpersonationResponseSchema = z.object({
  success: z.boolean(),
  sessionId: z.string(),
  message: z.string().optional(),
});
export type EndImpersonationResponseDTO = z.infer<typeof endImpersonationResponseSchema>;

// ========================================================================
// POST /api/admin/sourcing/backfill, POST /api/admin/outreach/refresh-replies
// ========================================================================

/**
 * GitHub issue #499 ("E2E: Admin dashboard — outreach metrics, company
 * filter, job table") "Actions" test-case group: `AdminDashboard`'s
 * "Backfill Sourcing" button. No real backend route exists for this yet
 * (there is no `apps/workers/src/api/admin/sourcing.ts` — see that
 * ticket's TODO in `AdminDashboard.tsx`'s module doc-comment); this schema
 * documents the response shape the button's mutation expects once one
 * does, and lets `e2e/admin-dashboard.spec.ts` stub a realistic response.
 */
export const backfillSourcingResponseSchema = z.object({
  triggered: z.boolean(),
  jobsQueued: z.number().int().nonnegative(),
});
export type BackfillSourcingResponse = z.infer<typeof backfillSourcingResponseSchema>;

/**
 * Same ticket's "Refresh Replies" button (`AdminDashboard.tsx`). Distinct
 * from the already-implemented per-job `useSyncReplies()` (`POST
 * /admin/jobs/:jobId/outreach/sync-replies`) — this is the dashboard-wide
 * "refresh all replies across every job" action the ticket's "Actions"
 * test-case group calls for.
 */
export const refreshRepliesResponseSchema = z.object({
  syncedCount: z.number().int().nonnegative(),
});
export type RefreshRepliesResponse = z.infer<typeof refreshRepliesResponseSchema>;
